import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "@phosphor-icons/react";

const LANGUAGES = [
  { code: "bs", label: "Bosanski", flag: "🇧🇦" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

interface LanguagePickerProps {
  variant?: "light" | "dark" | "footer" | "header";
  align?: "left" | "right";
  /** Header variant only: header sits transparent over a dark hero. */
  onDark?: boolean;
}

export function LanguagePicker({
  variant = "light",
  align = "right",
  onDark = false,
}: LanguagePickerProps = {}) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function selectLanguage(code: string) {
    i18n.changeLanguage(code);
    localStorage.setItem("lang", code);
    document.cookie = `lang=${code}; Path=/; Max-Age=31536000; SameSite=Lax`;
    setOpen(false);
  }

  // Pill variants (dark + footer) show the label and open upward; the bare
  // "light" variant is an icon-only circle that opens downward. The header
  // variant reads like a nav link (globe + language code) and opens downward.
  const isPill = variant === "dark" || variant === "footer";
  const isHeader = variant === "header";
  const buttonClass = isHeader
    ? "inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[0.8rem] font-medium uppercase tracking-wide transition-opacity duration-200 hover:opacity-70"
    : isPill
      ? "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-all duration-200 hover:scale-[1.03]"
      : "inline-flex size-10 items-center justify-center rounded-full border border-border/60 text-base transition-all duration-200 hover:scale-[1.05] hover:bg-muted/50";
  const buttonStyle = isHeader
    ? { color: onDark ? "oklch(100% 0 0 / 0.7)" : "oklch(45% 0.02 84)" }
    : variant === "dark"
      ? {
          borderColor: "oklch(30% 0.02 84)",
          background: "oklch(20% 0.02 84)",
          color: "oklch(75% 0.02 84)",
        }
      : variant === "footer"
        ? {
            borderColor: "oklch(90% 0.02 84 / 0.6)",
            background: "oklch(100% 0 0 / 0.7)",
            color: "oklch(42% 0.03 84)",
          }
        : undefined;
  const dropdownAlign = align === "left" ? "left-0" : "right-0";
  const dropdownPos = isPill ? `bottom-full mb-1.5 ${dropdownAlign}` : `${dropdownAlign} mt-1.5`;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={buttonClass}
        style={buttonStyle}
        aria-label={`Change language — current: ${current.label}`}
        title={current.label}
      >
        {isHeader ? (
          <>
            <Globe className="size-4" />
            <span className="leading-none">{current.code}</span>
          </>
        ) : (
          <>
            <span className="leading-none">{current.flag}</span>
            {isPill && <span className="font-medium">{current.label}</span>}
          </>
        )}
      </button>

      {open && (
        <div className={`absolute ${dropdownPos} w-40 rounded-lg border border-border/60 bg-background shadow-lg overflow-hidden z-50`}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => selectLanguage(lang.code)}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                lang.code === current.code
                  ? "bg-muted/60 text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
